import { useState } from 'react';
import { z } from 'zod';
import * as Sentry from '@sentry/react';
import { supabase } from '@/lib/supabase';
import { beregnBesparelse } from '@/lib/calculator';
import type { SvarMulighed } from '@/constants/questions';

const formSchema = z.object({
  navn: z.string().trim().min(2, { message: 'Indtast dit navn' }),
  email: z
    .string()
    .email({ message: 'Indtast en gyldig e-mailadresse' })
    .toLowerCase()
    .trim(),
});

type SubmitState = 'idle' | 'loading' | 'error';

interface EmailCaptureProps {
  budget: number;
  svar: Record<string, SvarMulighed>;
  onSuccess: (email: string) => void;
}

async function tilmeldMailerLite(navn: string, email: string): Promise<void> {
  const res = await fetch('/api/subscribe', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, navn }),
  });

  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `HTTP ${res.status}`);
  }
}

async function gemSession(
  email: string,
  navn: string,
  budget: number,
  svar: Record<string, SvarMulighed>,
  nyhedsbrev: boolean,
): Promise<void> {
  if (!supabase) return;

  const { lav, høj } = beregnBesparelse(budget, svar);

  const { error } = await supabase.from('calculator_sessions').insert({
    email,
    navn,
    budget,
    svar,
    resultat_lav: Math.round(lav),
    resultat_høj: Math.round(høj),
    nyhedsbrev,
  });

  if (error) {
    throw new Error(error.message);
  }
}

export function EmailCapture({ budget, svar, onSuccess }: EmailCaptureProps) {
  const [navn, setNavn] = useState('');
  const [email, setEmail] = useState('');
  const [nyhedsbrev, setNyhedsbrev] = useState(false);
  const [errors, setErrors] = useState<{ navn?: string; email?: string }>({});
  const [submitState, setSubmitState] = useState<SubmitState>('idle');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErrors({});

    const parsed = formSchema.safeParse({ navn, email });
    if (!parsed.success) {
      const fieldErrors: { navn?: string; email?: string } = {};
      for (const err of parsed.error.errors) {
        const field = err.path[0] as 'navn' | 'email';
        fieldErrors[field] = err.message;
      }
      setErrors(fieldErrors);
      return;
    }

    setSubmitState('loading');
    try {
      if (nyhedsbrev) {
        await tilmeldMailerLite(parsed.data.navn, parsed.data.email);
      }

      // Gem session non-blocking — fejl her blokerer ikke brugeren
      gemSession(parsed.data.email, parsed.data.navn, budget, svar, nyhedsbrev).catch(
        (err: unknown) => {
          Sentry.captureException(err);
          console.error('Supabase session-gem fejlede:', err);
        },
      );

      onSuccess(parsed.data.email);
    } catch {
      setSubmitState('error');
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-serif text-gray-900 mb-2">
          Dit resultat er klar
        </h2>
        <p className="text-gray-600">
          Indtast dit navn og din e-mail for at se dit personlige optimeringspotentiale.
          Vi deler aldrig dine oplysninger med andre.
        </p>
      </div>

      <form onSubmit={(e) => { void handleSubmit(e); }} noValidate className="space-y-4">

        {/* Navn */}
        <div>
          <label htmlFor="navn-input" className="block text-sm font-medium text-gray-700 mb-1.5">
            Navn
          </label>
          <input
            id="navn-input"
            type="text"
            autoComplete="given-name"
            value={navn}
            onChange={(e) => {
              setNavn(e.target.value);
              setErrors((prev) => ({ ...prev, navn: undefined }));
            }}
            placeholder="Dit fornavn"
            required
            aria-invalid={errors.navn != null}
            aria-describedby={errors.navn ? 'navn-error' : undefined}
            className={`w-full px-4 py-3 rounded-xl border-2 bg-white text-gray-900
              placeholder:text-gray-400 transition-colors
              focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2
              ${errors.navn ? 'border-red-400' : 'border-gray-200 focus-visible:border-brand'}`}
          />
          {errors.navn && (
            <p id="navn-error" role="alert" className="mt-1.5 text-sm text-red-600">
              {errors.navn}
            </p>
          )}
        </div>

        {/* Email */}
        <div>
          <label htmlFor="email-input" className="block text-sm font-medium text-gray-700 mb-1.5">
            E-mailadresse
          </label>
          <input
            id="email-input"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setErrors((prev) => ({ ...prev, email: undefined }));
            }}
            placeholder="din@email.dk"
            required
            aria-invalid={errors.email != null}
            aria-describedby={errors.email ? 'email-error' : undefined}
            className={`w-full px-4 py-3 rounded-xl border-2 bg-white text-gray-900
              placeholder:text-gray-400 transition-colors
              focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2
              ${errors.email ? 'border-red-400' : 'border-gray-200 focus-visible:border-brand'}`}
          />
          {errors.email && (
            <p id="email-error" role="alert" className="mt-1.5 text-sm text-red-600">
              {errors.email}
            </p>
          )}
        </div>

        {/* Opt-in — ikke pre-checket (GDPR) */}
        <label className="flex items-start gap-3 cursor-pointer group">
          <input
            type="checkbox"
            checked={nyhedsbrev}
            onChange={(e) => setNyhedsbrev(e.target.checked)}
            className="mt-0.5 w-4 h-4 rounded border-gray-300 accent-brand cursor-pointer
              focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2"
          />
          <span className="text-sm text-gray-600 group-hover:text-gray-800 transition-colors">
            Ja tak — send mig gratis tips og guides til byggeprocessen.
            Du kan afmelde dig når som helst.
          </span>
        </label>

        {submitState === 'error' && (
          <p role="alert" className="text-sm text-red-600">
            Noget gik galt. Prøv igen eller kontakt os på morten@droemmevillaen.dk
          </p>
        )}

        <button
          type="submit"
          disabled={submitState === 'loading'}
          className="w-full py-4 px-6 rounded-xl bg-brand text-white font-semibold
            hover:bg-brand-600 active:bg-brand-700 transition-colors
            focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2
            disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {submitState === 'loading' ? 'Henter resultat…' : 'Vis mit resultat →'}
        </button>

        <p className="text-xs text-center text-gray-400">
          Uvildigt og gratis. Ingen spam. Dine data deles ikke.
        </p>
      </form>
    </div>
  );
}
